import { useEffect, useState } from "react";
import { Copy, Mail, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "./api";
import { btnGhost, Spinner, StatusBadge, fmtDateTime } from "./ui";

export const AndroidWaitlistPage = () => {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/api/admin/messages", { params: { android: "1" } })
            .then((r) => setItems(r.data))
            .catch((e) => {
                toast.error(apiError(e));
                setItems([]);
            });
    }, []);

    const emails = (items || []).map((m) => m.email);
    const mailtoAll =
        emails.length > 0
            ? `mailto:?bcc=${encodeURIComponent(emails.join(","))}&subject=${encodeURIComponent("TheraOffice — Η έκδοση Android είναι εδώ!")}`
            : "#";

    const copyEmails = async () => {
        try {
            await navigator.clipboard.writeText(emails.join(", "));
            toast.success("Τα emails αντιγράφηκαν.");
        } catch {
            toast.error("Η αντιγραφή απέτυχε.");
        }
    };

    return (
        <div className="mx-auto max-w-6xl" data-testid="admin-android-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="flex items-center gap-3 text-2xl font-bold text-[#F2F5F6]">
                        <Smartphone size={24} className="text-[#5FBBAC]" />
                        Android Waitlist
                    </h1>
                    <p className="mt-1 text-sm text-[#697B80]">
                        {items === null
                            ? "Φόρτωση…"
                            : `${items.length} ${items.length === 1 ? "ενδιαφερόμενος έχει" : "ενδιαφερόμενοι έχουν"} δηλώσει ότι περιμένουν την έκδοση Android.`}
                    </p>
                </div>
                {items && items.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        <button onClick={copyEmails} className={btnGhost} data-testid="android-copy-emails">
                            <Copy size={14} /> Αντιγραφή emails
                        </button>
                        <a
                            href={mailtoAll}
                            className="inline-flex items-center gap-2 rounded-full bg-[#22A6C0] px-5 py-2.5 text-sm font-bold text-[#052536] transition-colors hover:bg-[#4CC9DC]"
                            data-testid="android-email-all"
                        >
                            <Mail size={15} /> Email σε όλους (BCC)
                        </a>
                    </div>
                )}
            </div>

            {items === null ? (
                <Spinner testId="android-loading" />
            ) : items.length === 0 ? (
                <div className="mt-16 flex flex-col items-center gap-3 text-center" data-testid="android-empty">
                    <Smartphone size={28} className="text-[#697B80]" />
                    <p className="text-sm text-[#697B80]">Κανείς δεν έχει δηλώσει ενδιαφέρον για Android ακόμη.</p>
                    <p className="max-w-md text-xs text-[#697B80]">
                        Όταν επισκέπτες τσεκάρουν το «Ενδιαφέρομαι για την έκδοση Android» στη φόρμα επικοινωνίας, θα εμφανίζονται εδώ.
                    </p>
                </div>
            ) : (
                <ul className="mt-6 space-y-3" data-testid="android-list">
                    {items.map((m) => (
                        <li
                            key={m.id}
                            className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#093448] p-4 sm:flex-row sm:items-center"
                            data-testid={`android-row-${m.id}`}
                        >
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-[#F2F5F6]">
                                    {m.name}
                                    {m.role && <span className="ml-2 text-xs font-normal text-[#697B80]">{m.role}</span>}
                                </p>
                                <a href={`mailto:${m.email}`} className="mt-0.5 block truncate text-xs text-[#4CC9DC] hover:text-[#F7EAB8]">
                                    {m.email}
                                </a>
                                <p className="mt-1.5 line-clamp-1 text-xs text-[#A3B3B8]">{m.message}</p>
                                <p className="mt-1 text-[11px] text-[#697B80]">{fmtDateTime(m.created_at)}</p>
                            </div>
                            <StatusBadge status={m.status} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
